import { Injectable, inject, computed, signal } from '@angular/core';

import { AdditionalInfo } from '../models/classes/additionalinfo';
import { Asset, AssetDetail } from '../models/classes/asset';
import {
  CurrentTelemetry as EMCurrentTelemetry,
  Sensor as EMSensor,
} from '../models/classes/sensor';
import { BuildingAreaSettings } from '../models/buildingareasettings.model';
import { Location } from '../models/classes/location';
import { LocationFilter } from '../models/classes/location.filter';
import { IFlatMarker } from '../models/marker.model';
import { INanoprecisePlantAssetDetailList, INanoPreciseToken } from '../models/classes/sensor';
import { SensorAdditionalField } from '../models/classes/sensoradditionalfield';
import { IOTObject } from '../models/object.model';
import { AssetsStore } from '../stores/assets.store';
import { Sensor } from '../models/classes/sensor';
import { MeasurementSystemSettings } from '../models/settings.model';
@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  rotatingEqWorkingFineSig = signal(false);
  rotatingEqWarningSig = signal(false);
  rotatingEqCriticalSig = signal(false);
  rotatingEqInactiveSig = signal(false);
  private assetsStore = inject(AssetsStore);
  public posts?: INanoprecisePlantAssetDetailList;
  public npToken?: INanoPreciseToken;

  // ✅ Expose computed signals for reactive access in components
  readonly plantsState = this.assetsStore.plantsState;
  readonly regionsState = this.assetsStore.regionsState;
  readonly unfilteredRegionsState = this.assetsStore.unfilteredRegionsState;
  readonly plants = this.assetsStore.plants;
  readonly filteredRegions = this.assetsStore.filteredRegions;
  readonly selectionPreset = this.assetsStore.selectionPreset;
  readonly loading = computed(() => this.assetsStore.loading());

  /**
   * Expose EM-specific signals
   */
  readonly currentAssetDetail = this.assetsStore.currentAssetDetail;
  readonly notifications = this.assetsStore.notifications;
  readonly filteredNotifications = this.assetsStore.filteredNotifications;
  readonly telemetryData = this.assetsStore.telemetryData;
  readonly mapboxKeys = this.assetsStore.mapboxKeys;
  readonly sensorsWithTelemetry = this.assetsStore.sensorsWithTelemetry;

  get Filtered(): boolean {
    return this.assetsStore.filtered();
  }

  get FilterSettingsCount(): number {
    return this.assetsStore.filterSettingsCount();
  }

  get debugMode(): boolean {
    return this.assetsStore.debugMode();
  }

  /**
   * All regions without filters applied
   * @returns unfiltered region list
   */
  get UnfilteredRegions(): Location[] {
    return this.assetsStore.unfilteredRegions();
  }

  get UnfilteredAreas(): Location[] {
    return this.assetsStore.unfilteredAreas();
  }

  logout() {
    this.assetsStore.clear();
  }

  getPlants() {
    return this.assetsStore.plantsState();
  }

  getSelectionPreset() {
    return this.assetsStore.selectionPreset();
  }

  getNextObjectId(): number {
    return this.assetsStore.getNextObjectId();
  }

  getAsset(id: number, locations: Location[]): Asset {
    return this.assetsStore.getAsset(id, locations);
  }

  getAssetHealth(id: number) {
    return this.assetsStore.getAssetHealth(id);
  }

  getChildrenByObjectID(parentId: number): IOTObject[] {
    return this.assetsStore.getChildrenByObjectID(parentId);
  }

  getPlantByAssetID(id: number, locations: Location[]): Location | undefined {
    return this.assetsStore.getPlantByAssetID(id, locations);
  }

  getSensorsByAssetID(assetId: number): Sensor[] {
    return this.assetsStore.getSensorsByAssetID(assetId);
  }

  getSensorBySensorID(sensorId: number): Sensor | undefined {
    return this.assetsStore.getSensorBySensorID(sensorId);
  }

  public async getRegions(forceReload = false): Promise<Location[]> {
    return await this.assetsStore.loadRegions(forceReload);
  }

  getSensorColorData(
    MacID: string,
    ModelID: string,
    AssetID: string,
    GatewayID: string
  ): Promise<any[]> {
    return this.assetsStore.getSensorColorData(MacID, ModelID, AssetID, GatewayID);
  }

  public postHtstData(
    colorCode: string,
    macId: string,
    modelId: string,
    isOccupied: boolean
  ): Promise<any> {
    return this.assetsStore.postHtstData(colorCode, macId, modelId, isOccupied);
  }

  public getNavigation(forceReload = false): Promise<Location[]> {
    return this.assetsStore.loadNavigation(forceReload);
  }

  public updateFilter(filter: LocationFilter) {
    this.assetsStore.updateFilter(filter);
  }

  public updateSelectionPreset(preset: Location[]) {
    this.assetsStore.updateSelectionPreset(preset);
  }

  // ✅ Delegate to store
  public resetLocationFilter() {
    this.assetsStore.resetLocationFilter();
  }

  // ✅ Delegate to store
  public clearFilter() {
    this.assetsStore.clearFilter();
  }

  // ✅ Delegate to store
  public getFilter() {
    return this.assetsStore.filter();
  }

  // ✅ Delegate to store - get filtered regions
  public getFilteredRegions(): Location[] {
    return this.assetsStore.filteredRegions();
  }

  // ✅ Delegate to store
  public updateSearchTerm(searchTerm: string) {
    this.assetsStore.updateSearchTerm(searchTerm);
  }

  // ✅ Delegate to store
  public getSearchTerm(): string {
    return this.assetsStore.searchTerm();
  }

  getCountryList(): Promise<any[]> {
    return this.assetsStore.getCountryList();
  }

  public async getSensors(forceReload = false): Promise<Sensor[]> {
    return await this.assetsStore.loadSensors(forceReload);
  }

  getBuildingAreaSettings(objectID: number): Promise<BuildingAreaSettings> {
    return this.assetsStore.getBuildingAreaSettings(objectID);
  }

  setBuildingAreaSettings(
    objectID: number,
    structureFloorZone: boolean,
    structureSectionCrossing: boolean,
    structureAssociatedEquipment: boolean
  ): Promise<boolean> {
    return this.assetsStore.setBuildingAreaSettings(
      objectID,
      structureFloorZone,
      structureSectionCrossing,
      structureAssociatedEquipment
    );
  }

  getParentCountryList(): Promise<any[]> {
    return this.assetsStore.getParentCountryList();
  }

  createPlant(
    plantName: string,
    areaID: number,
    areaName: string,
    imageFileName: string,
    imageData: string
  ): Promise<Location> {
    return this.assetsStore.createPlant(plantName, areaID, areaName, imageFileName, imageData);
  }

  updatePlant(plant: Location): Promise<Location[]> {
    return this.assetsStore.updatePlant(plant);
  }

  // ✅ Delegate to store
  deletePlant(plantID: number) {
    return this.assetsStore.deletePlant(plantID);
  }

  updatePlantMetadata(
    plantID: number,
    plantName: string,
    locationID: number,
    imageFileName: string,
    imageData: string,
    useDefaultImage: boolean,
    oldPicture: string
  ): Promise<{ StorageLink: string; PlantID: number }> {
    return this.assetsStore.updatePlantMetadata(
      plantID,
      plantName,
      locationID,
      imageFileName,
      imageData,
      useDefaultImage,
      oldPicture
    );
  }

  checkDeviceMapping(deviceValue: string, deviceType: string): Promise<{ isValid: boolean }> {
    return this.assetsStore.checkDeviceMapping(deviceValue, deviceType);
  }

  reserveGatewaySlot(gateway: string, isMT: boolean, isHtst: boolean): Promise<{ id: string }> {
    return this.assetsStore.reserveGatewaySlot(gateway, isMT, isHtst);
  }

  checkGatewayStatusMoxa(gateway: string): Promise<{ success: boolean }> {
    return this.assetsStore.checkGatewayStatusMoxa(gateway);
  }

  createSensor(
    parentID: number,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    qrDigits: number,
    qrEdge: string | number,
    sensorX: number,
    sensorY: number,
    equipmentSensorX: number,
    equipmentSensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string,
    previousSensorId: any,
    sensorType = 1003,
    sensorAdditionalFieldsValues: SensorAdditionalField[] = []
  ): Promise<any[]> {
    return this.assetsStore.createSensor(
      parentID,
      imageFileName,
      imageData,
      sensorName,
      qrDigits,
      qrEdge,
      sensorX,
      sensorY,
      equipmentSensorX,
      equipmentSensorY,
      sensorLabel,
      junctionBoxSlot,
      geoPosition,
      previousSensorId,
      sensorType,
      sensorAdditionalFieldsValues
    );
  }

  reconnectSensor(
    parentID: number,
    sensorID: any,
    useDefaultImage: boolean,
    oldPicture: string,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    qrDigits: number,
    qrEdge: number | string,
    sensorX: number,
    sensorY: number,
    equipmentSensorX: number,
    equipmentSensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string
  ): Promise<any[]> {
    return this.assetsStore.reconnectSensor(
      parentID,
      sensorID,
      useDefaultImage,
      oldPicture,
      imageFileName,
      imageData,
      sensorName,
      qrDigits,
      qrEdge,
      sensorX,
      sensorY,
      equipmentSensorX,
      equipmentSensorY,
      sensorLabel,
      junctionBoxSlot,
      geoPosition
    );
  }

  updateSensor(
    equipmentID: number,
    sensorID: number,
    sensorX: number,
    sensorY: number,
    sensorLabel: string,
    junctionBoxSlot: string,
    geoPosition: string,
    imageFileName: string,
    imageData: string,
    sensorName: string,
    useDefaultImage: boolean,
    oldPicture: string,
    subscription: string,
    isApplicableForAll = false
  ): Promise<any[]> {
    return this.assetsStore.updateSensor(
      equipmentID,
      sensorID,
      sensorX,
      sensorY,
      sensorLabel,
      junctionBoxSlot,
      geoPosition,
      imageFileName,
      imageData,
      sensorName,
      useDefaultImage,
      oldPicture,
      subscription,
      isApplicableForAll
    );
  }

  deleteSensor(sensorID: number): Promise<any[]> {
    return this.assetsStore.deleteSensor(sensorID);
  }

  removeDAUFromGatewayMoxa(sensorID: number): Promise<void> {
    return this.assetsStore.removeDAUFromGatewayMoxa(sensorID);
  }

  matchSensor(sensorID: number, qrNewEdgeDigits: string): Promise<any[]> {
    return this.assetsStore.matchSensor(sensorID, qrNewEdgeDigits);
  }

  matchAllSensors(qrOldEdgeDigits: string, qrNewEdgeDigits: string): Promise<any[]> {
    return this.assetsStore.matchAllSensors(qrOldEdgeDigits, qrNewEdgeDigits);
  }

  createEquipment(
    parentID: number,
    imageFileName: string,
    imageData: string,
    equipmentName: string,
    equipmentType: string,
    rotatingEqDefaultImageId: string
  ): Promise<any[]> {
    return this.assetsStore.createEquipment(
      parentID,
      imageFileName,
      imageData,
      equipmentName,
      equipmentType,
      rotatingEqDefaultImageId
    );
  }

  updateEquipment(
    equipmentID: number,
    imageFileName: string,
    imageData: string,
    equipmentName: string,
    equipmentType: string,
    useDefaultImage: boolean,
    oldPicture: string,
    rotatingEqDefaultImageId?: string
  ): Promise<any[]> {
    return this.assetsStore.updateEquipment(
      equipmentID,
      imageFileName,
      imageData,
      equipmentName,
      equipmentType,
      useDefaultImage,
      oldPicture,
      rotatingEqDefaultImageId
    );
  }

  updateEquipmentMarker(equipmentID: number, markers: IFlatMarker[]): Promise<any[]> {
    return this.assetsStore.updateEquipmentMarker(equipmentID, markers);
  }

  // ✅ Delegate to store
  deleteEquipment(equipmentID: number) {
    return this.assetsStore.deleteEquipment(equipmentID);
  }

  // ✅ Delegate to store
  getNanoEquipmentAccess(assetId: number) {
    return this.assetsStore.getNanoEquipmentAccess(assetId);
  }

  getAdditionalInfoTypes(): Promise<any> {
    return this.assetsStore.getAdditionalInfoTypes();
  }

  getAdditionalInfo(objectID: number): Promise<any[]> {
    return this.assetsStore.getAdditionalInfo(objectID);
  }

  updateAdditionalInfo(addInfoValues: AdditionalInfo[]): Promise<any[]> {
    return this.assetsStore.updateAdditionalInfo(addInfoValues);
  }

  getSensorAdditionalInfo(sensorID: number): Promise<any[]> {
    return this.assetsStore.getSensorAdditionalInfo(sensorID);
  }

  configureGateway(
    inletType: string,
    outletType: string,
    gateway: string,
    dau: string,
    slot: string
  ): Promise<{ success: boolean }> {
    return this.assetsStore.configureGateway(inletType, outletType, gateway, dau, slot);
  }

  configureGatewayMoxa(
    gateway: string,
    inletSerialNumber: string,
    inletPartNumber: string,
    outletSerialNumber: string,
    outletPartNumber: string,
    dau: string,
    dauPartNumber: string,
    slot: string
  ): Promise<{ success: boolean }> {
    return this.assetsStore.configureGatewayMoxa(
      gateway,
      inletSerialNumber,
      inletPartNumber,
      outletSerialNumber,
      outletPartNumber,
      dau,
      dauPartNumber,
      slot
    );
  }

  configureGatewayMoxaHTST(
    inletGateway: string,
    outletGateway: string,
    inletSerialNumber: string,
    inletPartNumber: string,
    outletSerialNumber: string,
    outletPartNumber: string,
    inletDau: string,
    outletDau: string,
    inletDauPartNumber: string,
    outletDauPartNumber: string,
    inletSensorSlot: string,
    outletSensorSlot: string,
    inletGatewayslot: string,
    outletGatewaySlot: string
  ): Promise<{ success: boolean }> {
    return this.assetsStore.configureGatewayMoxaHTST(
      inletGateway,
      outletGateway,
      inletSerialNumber,
      inletPartNumber,
      outletSerialNumber,
      outletPartNumber,
      inletDau,
      outletDau,
      inletDauPartNumber,
      outletDauPartNumber,
      inletSensorSlot,
      outletSensorSlot,
      inletGatewayslot,
      outletGatewaySlot
    );
  }

  checkGatewayConnectivity(gateway: string): Promise<{ success: boolean }> {
    return this.assetsStore.checkGatewayConnectivity(gateway);
  }

  checkGatewayConnectivityMoxa(gateway: string, dau: string): Promise<{ success: boolean }> {
    return this.assetsStore.checkGatewayConnectivityMoxa(gateway, dau);
  }

  checkDauConnectivity(gateway: string, slot: number): Promise<{ success: boolean }> {
    return this.assetsStore.checkDauConnectivity(gateway, slot);
  }

  public getNanoPreciseAssetsToken(): Promise<any[]> {
    return this.assetsStore.getNanoPreciseAssetsToken();
  }

  public getNanoPreciseAssetsDetail(assetId: number): Promise<any[]> {
    return this.assetsStore.getNanoPreciseAssetsDetail(assetId);
  }

  public setRotatingEquipmentDetail(post: any) {
    this.posts = post;
    this.assetsStore.setRotatingEquipmentDetail(post);
  }

  public getRotatingEquipmentDetail() {
    return this.posts || this.assetsStore.getRotatingEquipmentDetail();
  }

  public updateBuildingPlan(objectID: number, buildingPlan: string, buildingPlanName: string) {
    this.assetsStore.updateBuildingPlan(objectID, buildingPlan, buildingPlanName);
  }

  clearCache() {
    this.assetsStore.clear();
  }

  // ==================== Equipment Monitor Specific Methods ====================

  async getAssetEM(
    id: number,
    disableCache = false,
    measurementSystem?: MeasurementSystemSettings
  ): Promise<{
    asset: AssetDetail;
    plant: Location | undefined;
    breadcrumbsLoading: boolean;
  }> {
    return this.assetsStore.getAssetEM(id, disableCache, measurementSystem);
  }

  async getAssetDetail(
    id: number,
    disableCache = false,
    measurementSystem?: any
  ): Promise<{
    asset: any;
    plant: Location | undefined;
    breadcrumbsLoading: boolean;
  }> {
    return this.assetsStore.getAssetDetail(id, disableCache, measurementSystem);
  }

  subscribeToAsset(
    id: number,
    disableCache = false,
    measurementSystem: MeasurementSystemSettings,
    fn: (result: { asset: any; plant: Location | undefined; breadcrumbsLoading: boolean }) => void,
    err?: (error: any) => void
  ): void {
    // Load the data and invoke callbacks
    this.assetsStore
      .getAssetEM(id, disableCache, measurementSystem)
      .then((result) => {
        fn({
          asset: result.asset,
          plant: result.plant,
          breadcrumbsLoading: result.breadcrumbsLoading,
        });
      })
      .catch((error) => {
        if (err) {
          err(error);
        } else {
          console.error('Failed to get asset detail:', error);
        }
      });
  }

  async getSensorsEM(
    id: number,
    disableCache = false,
    measurementSystem?: MeasurementSystemSettings
  ): Promise<{
    sensors: EMSensor[];
    health: EMCurrentTelemetry[];
    steamtrap: EMCurrentTelemetry[];
    temperature: EMCurrentTelemetry | undefined;
  }> {
    return this.assetsStore.getSensorsEM(id, disableCache, measurementSystem);
  }

  async getSensorsWithTelemetry(id: number): Promise<{
    sensors: any[];
    health: any[];
    steamtrap: any[];
    temperature: any;
  }> {
    return this.assetsStore.getSensorsWithTelemetry(id);
  }

  async loadSensorsWithTelemetry(
    id: number,
    disableCache = false,
    measurementSystem?: MeasurementSystemSettings
  ): Promise<{
    sensors: any[];
    health: any[];
    steamtrap: any[];
    temperature: any;
  }> {
    return this.assetsStore.loadSensorsWithTelemetry(id, disableCache, measurementSystem);
  }

  subscribeToSensors(
    id: number,
    disableCache = false,
    measurementSystem: MeasurementSystemSettings,
    fn: (result: { sensors: Sensor[]; health: any; steamTrap: any; temperature: any }) => void,
    err?: (error: any) => void
  ): void {
    // Load the data and invoke callbacks
    this.assetsStore
      .loadSensorsWithTelemetry(id, disableCache, measurementSystem)
      .then((result) => {
        fn({
          sensors: result.sensors as Sensor[],
          health: result.health,
          steamTrap: result.steamtrap,
          temperature: result.temperature,
        });
      })
      .catch((error) => {
        if (err) {
          err(error);
        } else {
          console.error('Failed to load sensors with telemetry:', error);
        }
      });
  }

  async getNotifications(): Promise<any[]> {
    return this.assetsStore.getNotifications();
  }

  async loadNotificationsWithFilter(): Promise<{
    notifications: any[];
    filtered: boolean;
  }> {
    return this.assetsStore.loadNotificationsWithFilter();
  }

  subscribeToNotifications(
    fn: (result: { notifications: any[]; filtered: boolean }) => void,
    err?: (error: any) => void
  ): void {
    // Load notifications with filter and invoke callback
    this.assetsStore
      .loadNotificationsWithFilter()
      .then((result) => {
        fn(result);
      })
      .catch((error) => {
        if (err) {
          err(error);
        } else {
          console.error('Failed to load notifications:', error);
        }
      });
  }

  async getObjectHistory(objectId: number, sensorId: number, topN = 1000): Promise<any[]> {
    return this.assetsStore.getObjectHistory(objectId, sensorId, topN);
  }

  async getTelemetry(
    objectID: number,
    sensorID: number,
    measurementID: number,
    measurementSystem: MeasurementSystemSettings,
    timeframe?: { start: Date; end: Date; custom?: boolean }
  ): Promise<any> {
    return this.assetsStore.getTelemetry(
      objectID,
      sensorID,
      measurementID,
      measurementSystem,
      timeframe
    );
  }

  async getMapBoxKeys(): Promise<{
    satelliteView: string;
    mapView: string;
    geoCodingToken: string;
  }> {
    return this.assetsStore.getMapBoxKeys();
  }

  async getNanoPreciseRefreshToken(assetId: number): Promise<any> {
    return this.assetsStore.getNanoPreciseRefreshToken(assetId);
  }

  async goToNanoPreciseDashboard(assetId: number): Promise<any> {
    return this.assetsStore.goToNanoPreciseDashboard(assetId);
  }

  async getNanoPreciseAssetsHealthStatus(assetId: number): Promise<any[]> {
    return this.assetsStore.getNanoPreciseAssetsHealthStatus(assetId);
  }

  async getSensorSubscriptionDetails(sensorId: number): Promise<any> {
    return this.assetsStore.getSensorSubscriptionDetails(sensorId);
  }

  async getCustomerSensorSubscriptionDetails(objectID: number): Promise<any> {
    return this.assetsStore.getCustomerSensorSubscriptionDetails(objectID);
  }

  async getRegionsEM(forceReload = false): Promise<Location[]> {
    return this.assetsStore.getRegionsEM(forceReload);
  }

  async loadNavigationEM(forceReload = false): Promise<Location[]> {
    return this.assetsStore.getRegionsEM(forceReload);
  }

  findLocation(id: number): Location | undefined {
    return this.assetsStore.findLocation(id);
  }

  getNotificationsInstantly(): any[] {
    return this.assetsStore.notifications();
  }
}
